import { type ReactNode, useCallback, useEffect, useState } from 'react';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons/faExclamationCircle';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { App, type ButtonProps, type ModalFuncProps, theme } from 'antd';
import { t } from 'i18next';

type ConfirmModalButtonProps = Exclude<
  ButtonProps,
  'loading' | 'danger' | 'type'
>;

interface ConfirmModalProps {
  title?: ReactNode;
  content?: ReactNode;
  onConfirm?: () => void | Promise<unknown>;
  type?: 'confirm' | 'delete';
  icon?: IconDefinition | null;
  iconColor?: string;
  isLoading?: boolean;
  confirmText?: string;
  confirmProps?: ConfirmModalButtonProps;
  onCancel?: () => void;
  cancelProps?: ConfirmModalButtonProps;
  footer?: ModalFuncProps['footer'];
  styles?: ModalFuncProps['styles'];
}

export const useConfirmModal = (props: ConfirmModalProps) => {
  const {
    modal: { confirm },
  } = App.useApp();
  const { token } = theme.useToken();
  const [modalConfig, setModalConfig] = useState(props);

  const [modalInstance, setModalInstance] =
    useState<ReturnType<typeof confirm>>();

  const handleConfirm = useCallback(async () => {
    const result = modalConfig.onConfirm ? modalConfig.onConfirm() : () => null;
    if (result instanceof Promise) {
      await result;
    }
  }, [modalConfig]); // handles async functions if one is provided in prop (use async if you want loading to show on buttons)

  const handleCancel = useCallback(() => {
    if (modalConfig.onCancel) {
      modalConfig.onCancel();
    }
    if (modalInstance) {
      modalInstance.destroy();
      setModalInstance(undefined);
    }
  }, [modalInstance, modalConfig]);

  const configureModal = useCallback(
    ({
      title,
      content,
      confirmProps,
      isLoading,
      type,
      cancelProps,
      confirmText,
      icon,
      iconColor,
      ...config
    }: ConfirmModalProps): ModalFuncProps => ({
      title: title,
      content: content,
      icon: (
        <FontAwesomeIcon
          icon={icon ?? faExclamationCircle}
          color={iconColor ?? token.colorWarning}
          size="xl"
          style={{ paddingRight: token.paddingSM, paddingTop: '0.125rem' }}
        />
      ),
      okButtonProps: {
        ...confirmProps,
        loading: isLoading,
        type: 'primary',
        danger: type === 'delete',
        disabled: isLoading ? isLoading : confirmProps?.disabled,
      },
      async onOk() {
        return handleConfirm();
      }, // This is currently the only way to capture the loading status for the buttons
      okText: confirmText ?? t('common.buttons.confirm'),
      cancelButtonProps: {
        ...cancelProps,
        disabled: isLoading ? isLoading : cancelProps?.disabled,
      },
      cancelText: t('common.buttons.cancel'),
      onCancel: handleCancel,
      ...config,
    }),
    [handleCancel, handleConfirm, token]
  );

  const showModal = (config?: Partial<ConfirmModalProps>) => {
    const newConfig = { ...props, ...config };
    setModalConfig(newConfig);

    const instance = confirm(configureModal(newConfig));
    setModalInstance(instance);
  };

  useEffect(() => {
    if (modalInstance && props) {
      const updatedConfig = { ...modalConfig, ...props };
      modalInstance.update(configureModal(updatedConfig));
    }
  }, [modalConfig, modalInstance, configureModal, props]);

  const closeModal = useCallback(() => {
    if (modalInstance) {
      modalInstance.destroy();
      setModalInstance(undefined);
    }
  }, [modalInstance]);

  return {
    showModal,
    closeModal,
    modalVisible: !!modalInstance,
  };
};
